import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import Box from '@mui/material/Box';
import PortalTable, { PortalFieldDef } from '../util/PortalTable';

// ---------- Constants ----------

const TABLE = 'hr_user';

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Text', long_text: 'Long Text', number: 'Number', date: 'Date',
  select: 'Select', multi_select: 'Multi Select', checkbox: 'Checkbox',
  phone: 'Phone', email: 'Email', url: 'URL', currency: 'Currency',
};

const SYSTEM_FIELDS: { fieldKey: string; label: string; group: string; type: string; isCore: boolean }[] = [
  // Personal
  { fieldKey: 'second_name',           label: 'Middle Name',        group: 'Personal', type: 'text',   isCore: false },
  { fieldKey: 'date_of_birth',         label: 'Date of Birth',      group: 'Personal', type: 'date',   isCore: false },
  { fieldKey: 'sex',                   label: 'Gender',             group: 'Personal', type: 'select', isCore: false },
  { fieldKey: 'mobile_phone',          label: 'Mobile Phone',       group: 'Personal', type: 'phone',  isCore: false },
  // Address
  { fieldKey: 'city',                  label: 'City',               group: 'Address',  type: 'text',   isCore: false },
  { fieldKey: 'address',               label: 'Address',            group: 'Address',  type: 'text',   isCore: false },
  { fieldKey: 'postcode',              label: 'Postcode',           group: 'Address',  type: 'text',   isCore: false },
  // Work
  { fieldKey: 'job_title',             label: 'Job Title',          group: 'Work',     type: 'text',   isCore: false },
  { fieldKey: 'departmentId',          label: 'Department',         group: 'Work',     type: 'select', isCore: false },
  { fieldKey: 'contract_start_date',   label: 'Contract Start',     group: 'Work',     type: 'date',   isCore: false },
  { fieldKey: 'probation_start_date',  label: 'Probation Start',    group: 'Work',     type: 'date',   isCore: false },
  { fieldKey: 'probation_end_date',    label: 'Probation End',      group: 'Work',     type: 'date',   isCore: false },
  { fieldKey: 'notice_period',         label: 'Notice Period',      group: 'Work',     type: 'text',   isCore: false },
  // Emergency
  { fieldKey: 'emergency_contact_name',        label: 'Emergency Name',     group: 'Emergency',type: 'text',   isCore: false },
  { fieldKey: 'emergency_contact_relationship',label: 'Relationship',       group: 'Emergency',type: 'select', isCore: false },
  { fieldKey: 'emergency_contact_phone',       label: 'Emergency Phone',    group: 'Emergency',type: 'phone',  isCore: false },
  // Profile
  { fieldKey: 'avatarUrl',             label: 'Profile Photo',      group: 'Profile',  type: 'image',  isCore: false },
  { fieldKey: 'time_zone',             label: 'Timezone',           group: 'Profile',  type: 'select', isCore: false },
];

const WEIGHT_LABELS: Record<number, string> = { 1: 'Low', 2: 'Medium', 3: 'High' };

const MODAL_STYLE = {
  position: 'absolute' as const, top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 420, bgcolor: 'var(--bg-form)',
  border: 'var(--border-input)', borderRadius: '5px', boxShadow: 24, p: 3,
};

// ---------- WeightSelect ----------

function WeightSelect({ value, onChange, disabled }: {
  value: number; onChange: (v: number) => void; disabled?: boolean;
}) {
  return (
    <select
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      style={{
        fontSize: 11, padding: '2px 4px', borderRadius: 4,
        border: '1px solid var(--border-input)', background: 'var(--bg-input)',
        color: disabled ? 'var(--text-color-calendar-week)' : 'var(--text-color)',
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.4 : 1,
      }}
    >
      <option value={1}>Low</option>
      <option value={2}>Medium</option>
      <option value={3}>High</option>
    </select>
  );
}

// ---------- AddFieldModal ----------

interface ReplaceItem { option: string; count: number; replacement: string | null; }

function AddFieldModal({ open, onClose, onSaved, editing }: {
  open: boolean; onClose: () => void; onSaved: () => Promise<void>; editing?: any;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [name, setName] = useState('');
  const [group, setGroup] = useState('Other');
  const [type, setType] = useState('text');
  const [options, setOptions] = useState<string[]>([]);
  const [optionInput, setOptionInput] = useState('');
  const [isRequired, setIsRequired] = useState(false);
  const [isShown, setIsShown] = useState(true);
  const [step, setStep] = useState<'form' | 'replace'>('form');
  const [replaceItems, setReplaceItems] = useState<ReplaceItem[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.column_name ?? '');
      setGroup(editing.group ?? 'Other');
      setType(editing.type ?? 'text');
      setOptions(Array.isArray(editing.options) ? editing.options : []);
      setIsRequired(editing.isRequired ?? false);
      setIsShown(editing.isShown ?? true);
    } else {
      setName(''); setGroup('Other'); setType('text'); setOptions([]);
      setIsRequired(false); setIsShown(true);
    }
    setOptionInput('');
    setStep('form');
    setReplaceItems([]);
  }, [open, editing]);

  const addOption = () => {
    const val = optionInput.trim();
    if (!val) return;
    setOptions((prev) => prev.includes(val) ? prev : [...prev, val]);
    setOptionInput('');
  };

  const removeOption = (opt: string) => setOptions((prev) => prev.filter((o) => o !== opt));

  const buildPayload = () => ({
    table_name: TABLE,
    column_name: name.trim(),
    group,
    type,
    typeIds: null,
    options: ['select', 'multi_select'].includes(type) ? options : undefined,
    isRequired,
    isShown,
  });

  const persistField = async () => {
    try {
      if (editing?.id) { await axios.put(`fields/${editing.id}`, buildPayload()); }
      else { await axios.post('fields/add', buildPayload()); }
      enqueueSnackbar(editing ? t('Change') : t('Add'), { variant: 'success' });
      await onSaved();
      onClose();
    } catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
    finally { setSaving(false); }
  };

  const handleSave = async () => {
    if (!name.trim()) { enqueueSnackbar(t('Required'), { variant: 'error' }); return; }
    if (editing?.id && ['select', 'multi_select'].includes(type)) {
      const original: string[] = Array.isArray(editing.options) ? editing.options : [];
      const removed = original.filter((o) => !options.includes(o));
      if (removed.length > 0) {
        setSaving(true);
        try {
          const params = new URLSearchParams();
          removed.forEach((o) => params.append('options', o));
          const { data } = await axios.get(`fields/${editing.id}/option-usage?${params}`);
          const affected: ReplaceItem[] = removed
            .filter((o) => (data[o] ?? 0) > 0)
            .map((o) => ({ option: o, count: data[o], replacement: options[0] ?? null }));
          if (affected.length > 0) {
            setReplaceItems(affected);
            setStep('replace');
            setSaving(false);
            return;
          }
        } catch {}
        setSaving(false);
      }
    }
    setSaving(true);
    await persistField();
  };

  const handleConfirmReplace = async () => {
    setSaving(true);
    try {
      for (const item of replaceItems) {
        await axios.post(`fields/${editing.id}/replace-option`, { oldValue: item.option, newValue: item.replacement });
      }
      await persistField();
    } catch {
      enqueueSnackbar(t('Error'), { variant: 'error' });
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 500 } }}>
      <Fade in={open}>
        <Box sx={{ ...MODAL_STYLE, width: 420, maxHeight: '90vh', overflowY: 'auto' }}>

          {step === 'form' && (
            <>
              <div className="text-[15px] font-semibold mb-[14px] text-[var(--text-color)]">
                {editing ? t('Edit') : t('AddField')}
              </div>

              <div className="flex flex-col mb-[10px]">
                <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)]">
                  {t('Name')}
                </label>
                <input
                  className="w-full text-[13px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                  value={name} onChange={(e) => setName(e.target.value)} />
              </div>

              <div className="flex flex-col mb-[10px]">
                <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)]">
                  Block
                </label>
                <select
                  title="Block"
                  className="edit-select p-[5px] rounded-[5px] edit-input text-[13px]"
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                >
                  {['Personal', 'Address', 'Work', 'Emergency', 'Profile', 'Other'].map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col mb-[10px]">
                <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[4px] text-[var(--text-color-calendar-week)]">
                  {t('FieldType')}
                </label>
                <select title="Field Type" className="edit-select p-[5px] rounded-[5px] edit-input text-[13px]"
                  value={type} onChange={(e) => setType(e.target.value)}>
                  {Object.entries(FIELD_TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>

              {['select', 'multi_select'].includes(type) && (
                <div className="mb-[10px]">
                  <label className="text-[10px] uppercase tracking-wider font-semibold ml-[2px] mb-[6px] text-[var(--text-color-calendar-week)] block">
                    Options
                  </label>
                  <div className="flex gap-[6px] mb-[8px]">
                    <input
                      className="flex-1 text-[13px] py-[5px] px-[8px] rounded-[5px] bg-[var(--bg-input)] text-[var(--text-color)] timesheet-edit-input"
                      type="text" placeholder="Add option..." value={optionInput}
                      onChange={(e) => setOptionInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { addOption(); e.preventDefault(); } }} />
                    <button type="button" onClick={addOption} className="btn-primary">Add</button>
                  </div>
                  {options.length > 0 && (
                    <div className="flex flex-wrap gap-[5px]">
                      {options.map((opt) => (
                        <span key={opt}
                          className="inline-flex items-center gap-[4px] text-[12px] px-[9px] py-[2px] rounded-full font-medium"
                          style={{ backgroundColor: '#3b82f6', color: '#fff' }}>
                          {opt}
                          <button type="button" onClick={() => removeOption(opt)}
                            className="flex items-center justify-center w-[13px] h-[13px] rounded-full hover:bg-white/20 leading-none transition-colors">
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center gap-[16px] mb-[14px] text-[13px] text-[var(--text-color)]">
                <label className={`flex items-center gap-[6px] ${isShown ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'}`}>
                  <input type="checkbox" disabled={!isShown} checked={isRequired}
                    onChange={(e) => setIsRequired(e.target.checked)} />
                  {t('Required')}
                </label>
                <label className="flex items-center gap-[6px] cursor-pointer">
                  <input type="checkbox" checked={isShown} onChange={(e) => {
                    const v = e.target.checked;
                    setIsShown(v);
                    if (!v) setIsRequired(false);
                  }} />
                  {t('Visible')}
                </label>
              </div>

              <div className="flex gap-[8px]">
                <Button size="small" variant="contained" sx={{ flex: 1, fontSize: 12 }}
                  disabled={saving} onClick={handleSave}>
                  {saving ? '...' : editing ? t('Change') : t('Add')}
                </Button>
                <Button size="small" variant="outlined" sx={{ flex: 1, fontSize: 12 }} onClick={onClose}>
                  {t('Cancel')}
                </Button>
              </div>
            </>
          )}

          {step === 'replace' && (
            <>
              <div className="text-[15px] font-semibold mb-[4px] text-[var(--text-color)]">
                Removed options are in use
              </div>
              <div className="text-[12px] text-[var(--text-color-calendar-week)] mb-[16px]">
                These options are assigned to users. Choose a replacement or "Clear" to remove the value.
              </div>
              <div className="flex flex-col gap-[10px] mb-[20px]">
                {replaceItems.map((item) => (
                  <div key={item.option}
                    className="rounded-[6px] border border-[var(--border-input)] bg-[var(--btn-user-form)] p-[12px]">
                    <div className="flex items-center justify-between mb-[8px]">
                      <span className="inline-flex items-center text-[12px] px-[9px] py-[2px] rounded-full font-medium"
                        style={{ backgroundColor: '#ef4444', color: '#fff' }}>
                        {item.option}
                      </span>
                      <span className="text-[11px] text-[var(--text-color-calendar-week)]">
                        {item.count} user{item.count !== 1 ? 's' : ''} affected
                      </span>
                    </div>
                    <label className="text-[10px] text-[var(--text-color-calendar-week)] mb-[4px] block uppercase tracking-wider">
                      Replace with
                    </label>
                    <select
                      className="edit-select p-[5px] rounded-[5px] edit-input text-[13px] w-full"
                      value={item.replacement ?? '__clear__'}
                      onChange={(e) => setReplaceItems((prev) =>
                        prev.map((r) => r.option === item.option
                          ? { ...r, replacement: e.target.value === '__clear__' ? null : e.target.value }
                          : r)
                      )}>
                      <option value="__clear__">— Clear value —</option>
                      {options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <div className="flex gap-[8px]">
                <Button size="small" variant="contained" sx={{ flex: 1, fontSize: 12 }}
                  disabled={saving} onClick={handleConfirmReplace}>
                  {saving ? '...' : 'Apply & Save'}
                </Button>
                <Button size="small" variant="outlined" sx={{ flex: 1, fontSize: 12 }}
                  onClick={() => setStep('form')}>
                  Back
                </Button>
              </div>
            </>
          )}
        </Box>
      </Fade>
    </Modal>
  );
}

// ---------- Main component ----------

export default function HrProfileFields() {
  const { enqueueSnackbar } = useSnackbar();
  const [fieldConfigs, setFieldConfigs] = useState<any[]>([]);
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [addFieldOpen, setAddFieldOpen] = useState(false);
  const [editingField, setEditingField] = useState<any>(null);

  const load = useCallback(async () => {
    try {
      const [{ data: configs }, { data: fields }] = await Promise.all([
        axios.get('hr/profile-field-config'),
        axios.get(`fields/custom?table=${TABLE}`),
      ]);
      setFieldConfigs(Array.isArray(configs) ? configs : []);
      setCustomFields(Array.isArray(fields) ? fields : []);
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const getConfig = (fieldKey: string) => fieldConfigs.find((c) => c.fieldKey === fieldKey);

  const handleSystemToggle = async (fieldKey: string, prop: 'isVisible' | 'isRequired', current: boolean) => {
    const cfg = getConfig(fieldKey);
    let isVisible = prop === 'isVisible' ? !current : (cfg?.isVisible ?? true);
    let isRequired = prop === 'isRequired' ? !current : (cfg?.isRequired ?? false);
    if (prop === 'isVisible' && !isVisible) isRequired = false;
    const weight = cfg?.weight ?? 1;
    try {
      await axios.post('hr/profile-field-config', { fieldKey, isVisible, isRequired, weight });
      load();
    } catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
  };

  const handleWeightChange = async (fieldKey: string, weight: number) => {
    const cfg = getConfig(fieldKey);
    const isVisible = cfg?.isVisible ?? true;
    const isRequired = cfg?.isRequired ?? false;
    try {
      await axios.post('hr/profile-field-config', { fieldKey, isVisible, isRequired, weight });
      load();
    } catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
  };

  const handleCustomToggle = async (field: any, prop: 'isShown' | 'isRequired', current: boolean) => {
    const updates: Record<string, boolean> = { [prop]: !current };
    if (prop === 'isShown' && current) updates.isRequired = false;
    try { await axios.put(`fields/${field.id}`, updates); load(); }
    catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
  };

  const handleDeleteField = async (id: number) => {
    try { await axios.delete(`fields/${id}`); load(); }
    catch { enqueueSnackbar(t('Error'), { variant: 'error' }); }
  };

  const systemRows = SYSTEM_FIELDS.map((sf) => {
    const cfg = getConfig(sf.fieldKey);
    return {
      fieldKey: sf.fieldKey,
      label: sf.label,
      group: sf.group,
      isCore: sf.isCore,
      isVisible: cfg ? cfg.isVisible : true,
      isRequired: cfg ? cfg.isRequired : false,
      weight: cfg ? cfg.weight : 1,
    };
  });

  const systemFieldDefs: PortalFieldDef[] = [
    {
      key: 'group', label: t('Group'), type: 'text', editable: false, width: 90,
      render: (row) => <span className="text-[11px] opacity-50">{row.group}</span>,
    },
    {
      key: 'label', label: t('Name'), type: 'text', editable: false,
      render: (row) => (
        <span>
          {row.label}
          {row.isCore && <span className="text-[10px] opacity-30 ml-[5px]">core</span>}
        </span>
      ),
    },
    {
      key: 'weight', label: t('ProfileWeight'), type: 'text', editable: false, width: 100,
      render: (row) => (
        <WeightSelect
          value={row.weight}
          onChange={(v) => handleWeightChange(row.fieldKey, v)}
          disabled={!row.isVisible}
        />
      ),
    },
    {
      key: 'isRequired', label: t('Required'), type: 'boolean', variant: 'toggle',
      width: 80, align: 'right', editable: false,
      isDisabled: (row) => row.isCore || !row.isVisible,
    },
    {
      key: 'isVisible', label: t('Visible'), type: 'boolean', variant: 'toggle',
      width: 80, align: 'right', editable: false,
      isDisabled: (row) => row.isCore,
    },
  ];

  const customFieldDefs: PortalFieldDef[] = [
    {
      key: 'column_name', label: t('Name'), type: 'text', editable: false,
    },
    {
      key: 'group', label: 'Block', type: 'text', editable: false, width: 90,
      render: (f) => <span className="text-[11px] opacity-50">{f.group || 'Other'}</span>,
    },
    {
      key: 'values', label: t('Values'), type: 'text', width: 200, editable: false,
      render: (f) => {
        if (f.type === 'checkbox') return <span className="opacity-60 text-[12px]">Yes / No</span>;
        if (['select', 'multi_select'].includes(f.type) && Array.isArray(f.options)) {
          const display = f.options.join(', ');
          return <span className="text-[12px] opacity-70">{display.length > 40 ? display.slice(0, 40) + '…' : display}</span>;
        }
        return <span className="opacity-30">—</span>;
      },
    },
    {
      key: 'isRequired', label: t('Required'), type: 'boolean', variant: 'toggle',
      width: 80, align: 'right', editable: false,
      isDisabled: (row) => !row.isShown,
    },
    {
      key: 'isShown', label: t('Visible'), type: 'boolean', variant: 'toggle',
      width: 80, align: 'right', editable: false,
    },
  ];

  return (
    <>
      <PortalTable
        title={t('ProfileFields')}
        rows={systemRows}
        getKey={(row) => row.fieldKey}
        fields={systemFieldDefs}
        onToggleField={(row, fieldKey) =>
          handleSystemToggle(row.fieldKey, fieldKey as 'isVisible' | 'isRequired', (row as any)[fieldKey])
        }
      />

      <PortalTable
        title={t('CustomFields')}
        rows={customFields}
        getKey={(f) => f.id}
        fields={customFieldDefs}
        onAdd={() => { setEditingField(null); setAddFieldOpen(true); }}
        addLabel={`${t('Add')} ${t('Field')}`}
        onEdit={(f) => { setEditingField(f); setAddFieldOpen(true); }}
        onDelete={(f) => handleDeleteField(f.id)}
        onDeleteMany={async (ids) => {
          await Promise.all(ids.map((id) => axios.delete(`fields/${id}`)));
          load();
        }}
        onToggleField={(f, fieldKey) =>
          handleCustomToggle(f, fieldKey as 'isShown' | 'isRequired', (f as any)[fieldKey])
        }
      />

      <AddFieldModal
        open={addFieldOpen}
        onClose={() => setAddFieldOpen(false)}
        onSaved={load}
        editing={editingField}
      />
    </>
  );
}
